import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Make `/features#mcp` actually go to the MCP section.
 *
 * React Router does not scroll to a URL fragment. The browser's own handling
 * only fires on a full document load and only if the target already exists at
 * paint time — and every page here is behind `lazy()` + `<Suspense>`, so on a
 * client-side navigation the element does not exist yet and nothing happens.
 * The nav's Product menu had been quietly landing people at the top of the
 * page and leaving them to find the section themselves.
 *
 * Polls briefly rather than scrolling once, because the target appears only
 * after the lazy chunk resolves and renders. Gives up after ~1s: a hash that
 * matches nothing should do nothing, not retry forever.
 */
const RETRY_MS = 50;
const MAX_TRIES = 20;

export default function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash || hash.length < 2) return;
    // The OAuth callback puts a session_id in the fragment; it is not an anchor.
    if (hash.includes("session_id=")) return;

    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return; // a malformed fragment is not worth throwing over
    }

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    let tries = 0;
    let timer;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        return;
      }
      if (++tries < MAX_TRIES) timer = setTimeout(tick, RETRY_MS);
    };
    timer = setTimeout(tick, 0);
    return () => clearTimeout(timer);
  }, [hash, pathname]);

  return null;
}
